/*
 * The add-search, shared by onboarding and Manage fencers (direction §2, §12,
 * contract §3).
 *
 * ONE SEARCH OVER FENCERS AND CLUBS that narrows as you type. Contract §3 keeps
 * them as two endpoints because they are two shapes, but §2 describes one search
 * field — so the results are interleaved into one list and a club is an
 * EXPANDABLE row rather than a second search mode.
 *
 * WHY A CLUB EXPANDS RATHER THAN SELECTS. Contract §3: club members come back
 * "paginated (no bulk endpoint that invites select-all)". The reason is the cap: a
 * 10-fencer limit against a 75-member club makes select-all a trap, so the design
 * never offers it. There is deliberately no "add all" here and there must not be.
 *
 * THE PAGINATION IS REAL, and its denominator is not invented. "Show more" appears
 * only while fewer members are loaded than the club row already announced, and
 * that count comes from a query with the SAME predicate as the members query
 * (view_roster.sql: `merged_into IS NULL` and either club column). So "18
 * remaining" is arithmetic over two numbers the server computed the same way —
 * which is what separates it from the field-size denominators contract §8 bars
 * outright, where the two numbers come from different places and drift.
 *
 * RATINGS RENDER AS RATED WEAPONS ONLY. Contract §3: "render rated weapons only",
 * and the server never sends 'U' — unrated is an absence, and absences are
 * unmarked (principle: show what exists). So a fencer with one rating shows one
 * chip, not three with two blanks.
 *
 * MEMBER ID IS NULLABLE BY DESIGN, ~8% absent (contract §3, invariant 8). It
 * renders when present and nothing when not; it is never a required
 * disambiguator, because the fencers most likely to lack one are exactly the
 * unsanctioned-tournament competitors invariant 8 exists to protect.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { api, list, type ClubResult, type FencerResult } from "../lib/api";
import { countryName, flagEmoji } from "../lib/country";
import { dateLocale } from "../lib/format";
import styles from "./FencerSearch.module.css";

/** One expanded club's accumulated pages, and how many members it has in total. */
interface ClubPage {
  id: string;
  total: number;
  members: FencerResult[];
  loading: boolean;
}

export interface FencerSearchProps {
  /** The fencers picked so far. Owned by the caller, so it survives navigation. */
  chosen: FencerResult[];
  onChange: (next: FencerResult[]) => void;
  /**
   * The cap. Sent by the server so the arithmetic has one source, and runtime
   * configuration there (decision 889), so there is no default here: absent —
   * the server could not read it — the count line and the at-cap block are not
   * rendered, and the server's 409 is the only limit.
   */
  cap?: number;

  /**
   * The caller's commit control, rendered WITH the selection rather than after
   * the results (decision 326).
   *
   * WHY IT IS A SLOT AND NOT SOMETHING THIS COMPONENT OWNS. The button lived
   * below the whole search, which put it below the results list — and a search
   * for a common surname returns enough rows to push it off the bottom of a
   * phone, so the flow became: pick someone, scroll past thirty names you have
   * already rejected, commit. Moving it up to sit under the chips means it is on
   * screen the instant there is anything to commit, however long the list is.
   *
   * The arithmetic it displays ("3 selected · 8 of 10 after adding") needs the
   * caller's roster count, which this component does not have and should not —
   * so the caller keeps the control and this component only decides WHERE it
   * goes. Rendered inside the chosen block, so it appears and disappears with the
   * selection without a second condition to keep in step.
   */
  commit?: ReactNode;

  /**
   * The fencers already on the list being edited. Picking one is a no-op the
   * caller skips at commit, so it takes none of the room left either.
   */
  memberIds?: ReadonlySet<string>;

  /**
   * Every list each fencer is already on, by name — the open list and the
   * account's others. A row whose fencer appears here says "On list '{list}'"
   * once per list, so a coach sees a fencer is already on a DIFFERENT list
   * before adding them to this one.
   *
   * THE ROW STAYS SELECTABLE and the indicator NAMES THE LIST rather than being
   * a bare tick, which would read as a picked row (`aria-pressed`). Onboarding
   * passes neither prop: no list exists yet.
   */
  onLists?: ReadonlyMap<string, readonly string[]>;
}

/**
 * A rating's year as a rating is written: its last two digits, so "E26"
 * rather than "E2026" (decision 988, Kevin: the standard form, as the app
 * already shows it).
 */
export function ratingYear(year: number): string {
  return year > 0 ? String(year % 100).padStart(2, "0") : "";
}

export function FencerSearch({
  chosen,
  onChange,
  cap,
  commit,
  memberIds,
  onLists,
}: FencerSearchProps) {
  const { t } = useTranslation();

  /** "On list 'A' · On list 'B'" for a fencer already on lists, or null. */
  const onList = (f: FencerResult) => {
    const names = onLists?.get(f.fencer_id) ?? [];
    return names.length > 0 ? names.map((list) => t("search.onList", { list })).join(" · ") : null;
  };

  const [query, setQuery] = useState("");
  const [fencers, setFencers] = useState<FencerResult[]>([]);
  const [clubs, setClubs] = useState<ClubResult[]>([]);
  const [searching, setSearching] = useState(false);

  /*
   * ONE club expanded at a time, holding its own accumulated page.
   *
   * One rather than a set, because §2's club expansion "expands in place" inside a
   * single search result list: two open clubs would push the second one's members
   * below the fold on a phone, and the flow is "look at this club, pick from it,
   * move on". Collapsing and re-expanding refetches from zero, which is correct —
   * a stale first page held across a collapse is a cache, and this component is
   * deliberately not one.
   */
  const [club, setClub] = useState<ClubPage | null>(null);

  // A pick already on the list adds nothing, so it takes none of the room.
  const counted = memberIds ? chosen.filter((c) => !memberIds.has(c.fencer_id)).length : chosen.length;
  const atCap = cap !== undefined && counted >= cap;

  /*
   * DEBOUNCED, and the number is a politeness decision rather than a UI one.
   * Every keystroke is a trigram query over 3,000 fencers plus a club search;
   * 250ms is long enough that typing a name is one query rather than eleven, and
   * short enough that it still feels like narrowing rather than submitting.
   */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setFencers([]);
      setClubs([]);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timer = setTimeout(() => {
      Promise.all([api.searchFencers(q), api.searchClubs(q)])
        .then(([f, c]) => {
          if (cancelled) return;
          setFencers(list(f.fencers));
          setClubs(list(c.clubs));
        })
        .catch(() => {
          if (!cancelled) {
            setFencers([]);
            setClubs([]);
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  function toggle(f: FencerResult) {
    const already = chosen.some((c) => c.fencer_id === f.fencer_id);
    if (already) {
      onChange(chosen.filter((c) => c.fencer_id !== f.fencer_id));
      return;
    }
    // AT-CAP BLOCKED COMMIT: the add is refused rather than accepted-and-trimmed,
    // because silently dropping the eleventh pick is the failure a coach would not
    // notice until a tournament. The server enforces it too (roster.go) — this is
    // the courtesy, not the guard.
    if (atCap) return;
    onChange([...chosen, f]);
  }

  async function expandClub(row: ClubResult) {
    if (club?.id === row.club_id) {
      setClub(null);
      return;
    }
    setClub({ id: row.club_id, total: row.member_count, members: [], loading: true });
    await loadClubPage(row.club_id, row.member_count, []);
  }

  async function loadClubPage(id: string, total: number, sofar: FencerResult[]) {
    try {
      const body = await api.clubMembers(id, sofar.length);
      const page = list(body.fencers);
      setClub((current) => {
        // The club may have been collapsed, or a different one expanded, while
        // this page was in flight. Dropping the response is the only correct
        // answer: appending it would show one club's members under another's name.
        if (!current || current.id !== id) return current;
        return { id, total, members: [...sofar, ...page], loading: false };
      });
    } catch {
      setClub((current) =>
        current && current.id === id ? { ...current, loading: false } : current,
      );
    }
  }

  return (
    <div className={styles.wrap}>
      <label className={styles.field}>
        <span className={styles.label}>{t("search.label")}</span>
        <input
          className={styles.input}
          type="search"
          name="fencer-search"
          // Not a credential field, so no autocomplete token applies; `off` here
          // is correct and harmless because a browser suggesting previous searches
          // over live results is noise.
          autoComplete="off"
          autoCapitalize="words"
          spellCheck={false}
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      {/* THE ARITHMETIC, always visible once anything is chosen (§2's "N of 10"). */}
      {chosen.length > 0 ? (
        <div className={styles.chosen}>
          {cap !== undefined ? (
            <p className={styles.count}>{t("search.countOf", { count: counted, cap })}</p>
          ) : null}
          <ul className={styles.chips}>
            {chosen.map((f) => (
              <li key={f.fencer_id}>
                <button className={styles.chip} type="button" onClick={() => toggle(f)}>
                  {f.name}
                  {/* The × is decorative; the accessible name says what happens. */}
                  <span aria-hidden="true"> ×</span>
                  <span className="tc-sr-only">{t("search.remove", { name: f.name })}</span>
                </button>
              </li>
            ))}
          </ul>
          {atCap ? <p className={styles.atCap}>{t("search.atCap", { cap })}</p> : null}
          {commit}
        </div>
      ) : null}

      {query.trim().length >= 2 ? (
        <div className={styles.results}>
          {searching && fencers.length === 0 && clubs.length === 0 ? (
            <p className={styles.muted}>{t("search.searching")}</p>
          ) : null}

          {!searching && fencers.length === 0 && clubs.length === 0 ? (
            // An honest empty result, and specifically not "no results found" with
            // a shrug illustration. Search is over canonical fencers only
            // (contract §3): an unresolved alias is not addable, so a name that
            // only FTL has seen genuinely is not here yet.
            <p className={styles.muted}>{t("search.nothing")}</p>
          ) : null}

          {fencers.length > 0 ? (
            <ul className={styles.list}>
              {fencers.map((f) => {
                const picked = chosen.some((c) => c.fencer_id === f.fencer_id);
                return (
                  <li key={f.fencer_id}>
                    <button
                      className={styles.row}
                      type="button"
                      onClick={() => toggle(f)}
                      // The pressed state, not a checkbox: the row IS the control,
                      // and a screen reader needs to hear whether it is on.
                      aria-pressed={picked}
                      // A READ-ONLY SELECTOR HOOK for the screenshot script, on
                      // the unresolved queue's `data-alias` precedent (decisions
                      // 314, 323). It exists because the alternatives are not
                      // safe: this row has no distinguishing role or accessible
                      // name — its name is a fencer's, which differs per
                      // database — and reaching for a positional or ARIA-state
                      // selector instead is what made a capture pass click the
                      // commit button and finish somebody's onboarding.
                      data-fencer={f.fencer_id}
                      disabled={!picked && atCap}
                    >
                      <FencerRowMain f={f} meta={[f.club, f.division]} onList={onList(f)} />
                      <span className={styles.rowEnd}>
                        {list(f.ratings).map((r) => (
                          <span key={r.weapon} className={`tc-num ${styles.rating}`}>
                            {t(`weapon.${r.weapon}`)} {r.letter}
                            {ratingYear(r.year)}
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {clubs.length > 0 ? (
            <ul className={styles.list}>
              {clubs.map((c) => (
                <li key={c.club_id}>
                  <button
                    className={styles.row}
                    type="button"
                    onClick={() => void expandClub(c)}
                    // A collapse button with correct ARIA, per the non-negotiables.
                    aria-expanded={club?.id === c.club_id}
                  >
                    <span className={styles.rowMain}>
                      <span className={styles.name}>{c.name}</span>
                      <span className={styles.meta}>
                        {t("search.memberCount", { count: c.member_count })}
                      </span>
                    </span>
                  </button>

                  {club?.id === c.club_id ? (
                    <ClubPanel
                      page={club}
                      chosen={chosen}
                      atCap={atCap}
                      onList={onList}
                      onToggle={toggle}
                      onMore={() => void loadClubPage(club.id, club.total, club.members)}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A result row's text: the name with its country's flag, the facts line, and
 * the ids line.
 *
 * THE COUNTRY IS NAMED only for a fencer FIE or EFC knows, after the club,
 * because for everyone else the flag already says it and it is almost always
 * the US. THE IDS LINE appears only when two or more of USA Fencing's, FIE's
 * and EFC's ids are known: one id disambiguates nothing a name and club do
 * not, but two say the record joins sources, which is what a coach checks.
 */
function FencerRowMain({
  f,
  meta,
  onList,
}: {
  f: FencerResult;
  /** The leading facts (club, division), each shown only if it exists. */
  meta: (string | null | undefined)[];
  onList: string | null;
}) {
  const { t } = useTranslation();
  const flag = flagEmoji(f.country);
  // The catalogue's Intl locale, as dates use (Serbian is "sr-Latn").
  const country = countryName(f.country, dateLocale());
  const international = Boolean(f.fie_id || f.efc_id);
  const facts = [...meta, international ? country : null, onList].filter(Boolean).join(" · ");
  const ids = (
    [
      ["usafencing", f.member_id],
      ["fie", f.fie_id],
      ["efc", f.efc_id],
    ] as const
  ).filter(([, id]) => Boolean(id));

  return (
    <span className={styles.rowMain}>
      <span className={styles.name}>
        {f.name}
        {flag ? (
          <span className={styles.flag} role="img" aria-label={country} title={country}>
            {flag}
          </span>
        ) : null}
      </span>
      {facts ? <span className={styles.meta}>{facts}</span> : null}
      {ids.length >= 2 ? (
        <span className={`tc-num ${styles.meta}`}>
          {ids.map(([source, id]) => t("search.sourceId", { source: t(`source.${source}`), id })).join(" · ")}
        </span>
      ) : null}
    </span>
  );
}

/**
 * An expanded club's members, paginated.
 *
 * NO "ADD ALL", and the absence is the design (§2). What replaces it is a page at
 * a time with the remainder stated, so a coach browsing a 75-member club sees that
 * it is 75 members before they start rather than after they have picked ten.
 */
function ClubPanel({
  page,
  chosen,
  atCap,
  onList,
  onToggle,
  onMore,
}: {
  page: ClubPage;
  chosen: FencerResult[];
  atCap: boolean;
  onList: (f: FencerResult) => string | null;
  onToggle: (f: FencerResult) => void;
  onMore: () => void;
}) {
  const { t } = useTranslation();

  const remaining = page.total - page.members.length;

  return (
    <ul className={styles.nested}>
      {page.members.map((f) => {
        const picked = chosen.some((x) => x.fencer_id === f.fencer_id);
        const member = onList(f);
        return (
          <li key={f.fencer_id}>
            <button
              className={styles.row}
              type="button"
              onClick={() => onToggle(f)}
              aria-pressed={picked}
              disabled={!picked && atCap}
            >
              <FencerRowMain f={f} meta={[]} onList={member} />
              <span className={styles.rowEnd}>
                {list(f.ratings).map((r) => (
                  <span key={r.weapon} className={`tc-num ${styles.rating}`}>
                    {t(`weapon.${r.weapon}`)} {r.letter}
                    {ratingYear(r.year)}
                  </span>
                ))}
              </span>
            </button>
          </li>
        );
      })}

      {page.loading ? <li className={styles.muted}>{t("search.searching")}</li> : null}

      {/*
        A club whose member count is real but whose member rows are all merged
        away, or a fetch that failed: neither is an error worth an alert, and
        saying nothing here would leave an empty disclosure open. The count on the
        row above is still the honest number.
      */}
      {!page.loading && page.members.length === 0 ? (
        <li className={styles.muted}>{t("search.noClubMembers")}</li>
      ) : null}

      {!page.loading && remaining > 0 ? (
        <li>
          {/*
            Text-red, not filled: filled red is the screen's ONE primary action
            (principle 3), and on Manage fencers that is Add. A page-loader inside
            a disclosure is about as secondary as an action gets.
          */}
          <button className={styles.more} type="button" onClick={onMore}>
            {t("search.showMore", { count: remaining })}
          </button>
        </li>
      ) : null}
    </ul>
  );
}
